import { FormField, ChipFormField } from "./components";
import { styles } from "./styles";
import {
  validatePayRange,
  type SalaryPreference,
  filterPayInput,
  isValidPayInput,
  parseLocaleNumber,
  getLocaleNumberInfo,
} from "./validation";
import { Text, Button, Alert, Flex } from "../../../mads";
import { ChevronDown, ChevronUp, Hide } from "../../../mads/icons";
import { useI18n, useLocale, type GettextInterface } from "../../I18n";
import { serializeLocaleToBCP47 } from "../../I18n/Locale";
import { getLocaleCurrency } from "../../util/salary/getLocaleCurrency";
import { getPayTypesToLabels } from "../../util/salary/getPayTypesToLabels";
import { parseLocaleCurrencyAmount } from "../../util/salary/parseLocaleCurrencyAmount";
import { SalaryType } from "../../util/salary/SalaryType";

import { colors } from "@indeed/ifl-tokens/react-native";
import React, { useMemo, useState, useCallback } from "react";
import { TextInput, type TextInputProps } from "react-native";

// Re-export SalaryPreference for consumers
export type { SalaryPreference } from "./validation";

export interface MinimumPayProps {
  value?: SalaryPreference;
  onChange: (salaryPreference: SalaryPreference) => void;
  showErrors?: boolean;
  showEmployerAlert?: boolean;
  variant?: "default" | "compact";
}

// PayInput component for the currency input field
export const PayInput = ({ invalid, ...inputProps }: TextInputProps & { invalid: boolean }) => {
  const localeObj = useLocale();
  const locale = serializeLocaleToBCP47(localeObj);
  const { hasRighthandSymbol, currencySymbol } = getLocaleCurrency(locale);
  const currencyElement = <Text sx={styles.payInputCurrency}>{currencySymbol}</Text>;
  const wrapperStyles = {
    ...styles.payInputWrapper,
    ...(invalid ? styles.payInputWrapperInvalid : {}),
  };

  return (
    <Flex sx={wrapperStyles}>
      {!hasRighthandSymbol && currencyElement}
      <TextInput style={styles.payInput} inputMode="decimal" {...inputProps} />
      {hasRighthandSymbol && currencyElement}
    </Flex>
  );
};

// EmployersPayInfoAlert component
export const EmployersPayInfoAlert = () => {
  const gt = useI18n();

  return (
    <Alert
      icon={<Hide color={colors.foregroundStatusInfo} aria-hidden />}
      status="info"
      explainerText={gt.gettext("Not shown to employers.")}
    />
  );
};

// PayPeriodSelect component for selecting the pay period
export const PayPeriodSelect = ({
  invalid,
  value,
  onChange,
  errorText,
  showRequiredIndicator = false,
}: {
  invalid?: boolean;
  value?: SalaryPreference;
  onChange: (salaryPreference: SalaryPreference) => void;
  errorText?: string;
  showRequiredIndicator?: boolean;
}) => {
  const PAY_OPTIONS_ORDER = useMemo(
    () => [
      SalaryType.Hourly,
      SalaryType.Yearly,
      SalaryType.Monthly,
      SalaryType.Weekly,
      SalaryType.Daily,
    ],
    [],
  );

  const INITIAL_SHOW_COUNT = 2;
  const gt = useI18n();
  const amount = value?.amount ?? "";
  const salaryType = value?.salaryType ?? "";

  const payPeriodLabel = gt.pgettext("Pay period form field label", "Pay period");
  const payTypesToLabels = getPayTypesToLabels(gt);

  // The pay type is pre-selected based on the job's pay type.
  // If the selected pay type is below the fold, show all on load.
  const shouldBeExpandedOnInitialLoad =
    PAY_OPTIONS_ORDER.slice(INITIAL_SHOW_COUNT).filter(
      (payType) => payType === (salaryType as SalaryType),
    ).length > 0;

  const [isExpanded, setIsExpanded] = useState(shouldBeExpandedOnInitialLoad);

  const selectSalaryType = useCallback(
    (selected: string) => onChange({ amount, salaryType: selected, sentiment: value?.sentiment }),
    [amount, onChange, value?.sentiment],
  );

  const items = useMemo(
    () =>
      PAY_OPTIONS_ORDER.slice(0, isExpanded ? undefined : INITIAL_SHOW_COUNT).map((payType) => ({
        value: payType,
        label: `${payTypesToLabels[payType][0].toUpperCase()}${payTypesToLabels[payType].slice(1)}`,
      })),
    [isExpanded, payTypesToLabels, PAY_OPTIONS_ORDER],
  );

  return (
    <Flex sx={styles.inputBox}>
      <ChipFormField
        label={payPeriodLabel}
        invalid={!!invalid}
        size="sm"
        errorText={errorText}
        showRequiredIndicator={showRequiredIndicator}
        items={items}
        checkedValue={salaryType}
        onChange={selectSalaryType}
      />
      <Button
        variant="text"
        onPress={() => setIsExpanded(!isExpanded)}
        iconAfter={isExpanded ? <ChevronUp aria-hidden /> : <ChevronDown aria-hidden />}
        sx={{ paddingLeft: 0, paddingRight: 0 }}
      >
        {isExpanded ? gt.gettext("Show less pay periods") : gt.gettext("Show more pay periods")}
      </Button>
    </Flex>
  );
};

// Helper function to get error messages
export function getPayErrorMessages({
  gt,
  value,
  showErrors,
  locale,
}: {
  value?: SalaryPreference;
  gt: GettextInterface;
  showErrors?: boolean;
  locale: string;
}) {
  const isAmountZero = !!value?.amount && parseLocaleCurrencyAmount(value?.amount, locale) === 0;
  const payRangeValidationError = (value && validatePayRange(value, locale, gt)) || undefined;

  return {
    fieldsetError: payRangeValidationError,
    inputError:
      showErrors && (!value?.amount || isAmountZero) && !payRangeValidationError
        ? isAmountZero
          ? gt.pgettext(
              "Minimum pay greater than zero error text",
              "Enter an amount greater than 0",
            )
          : gt.pgettext("Minimum pay required error text", "Minimum pay is required")
        : "",
    selectError:
      showErrors && !value?.salaryType && !payRangeValidationError
        ? gt.pgettext("Pay period error text", "Pay period is required")
        : "",
  };
}

// Main MinimumPay component
export const MinimumPay: React.FC<MinimumPayProps> = ({
  value,
  onChange,
  showErrors,
  showEmployerAlert = true,
  variant = "default",
}) => {
  const localeObj = useLocale();
  const locale = serializeLocaleToBCP47(localeObj);
  const gt = useI18n();
  const basePayLabel = gt.gettext("Minimum base pay");

  const { fieldsetError, inputError, selectError } = getPayErrorMessages({
    gt,
    value,
    showErrors,
    locale,
  });

  const formattedValue = useMemo(() => {
    if (!value?.amount) {
      return "";
    }
    // Use Intl.NumberFormat for consistent formatting
    const { formatter } = getLocaleNumberInfo(locale);
    const numericAmount = parseLocaleNumber(value.amount, locale);
    return isNaN(numericAmount) ? value.amount : formatter.format(numericAmount);
  }, [value?.amount, locale]);

  const handleAmountChange = useCallback(
    (text: string) => {
      // Use Intl.NumberFormat-based validation functions
      if (isValidPayInput(text, locale)) {
        const filteredAmount = filterPayInput(text, locale);
        onChange({
          amount: filteredAmount,
          salaryType: value?.salaryType,
          sentiment: value?.sentiment,
        });
      }
    },
    [onChange, value?.salaryType, value?.sentiment, locale],
  );

  const containerStyles = variant === "compact" ? styles.compactContainer : styles.defaultContainer;

  return (
    <Flex sx={containerStyles}>
      {showEmployerAlert && <EmployersPayInfoAlert />}
      <FormField errorText={fieldsetError} invalid={!!fieldsetError}>
        <Flex sx={styles.updatedWrapper}>
          <Flex sx={{ marginBottom: 4 }}>
            <FormField
              errorText={inputError}
              showRequiredIndicator
              invalid={!!inputError || !!fieldsetError}
              label={basePayLabel}
            >
              <PayInput
                value={formattedValue}
                onChangeText={handleAmountChange}
                aria-required
                aria-label={basePayLabel}
                invalid={!!inputError || !!fieldsetError}
              />
            </FormField>
          </Flex>
          <PayPeriodSelect
            invalid={!!selectError || !!fieldsetError}
            value={value}
            onChange={onChange}
            errorText={selectError}
            showRequiredIndicator={true}
          />
        </Flex>
      </FormField>
    </Flex>
  );
};

export default MinimumPay;
